import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';
type HeatmapTab = 'Deviations' | 'Disagreements';
type AggregationType = 'Full (hourly)' | 'Daily';
export function HeatmapPanel() {
  const [activeTab, setActiveTab] = useState<HeatmapTab>('Deviations');
  const [aggregation, setAggregation] = useState<AggregationType>('Full (hourly)');
  // Mock data for heatmap
  const hours = Array.from({
    length: 24
  }, (_, i) => i);
  // Generate array of dates for daily view (last 14 days)
  const days = Array.from({
    length: 14
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (13 - i));
    return date;
  });
  const models = ['EPT-2', 'EPT-2 Early', 'EC IFS', 'GFS'];
  // Generate random heatmap data
  const getData = (modelIndex: number, hourIndex: number) => {
    // Create patterns based on model and hour
    const base = Math.sin(hourIndex / 3) * 2;
    const noise = (Math.random() - 0.5) * 2;
    const modelBias = modelIndex === 0 ? 0.5 : modelIndex === 3 ? -1 : 0;
    return base + noise + modelBias;
  };
  const getColor = (value: number) => {
    // Color scale: Blue (negative) -> White (neutral) -> Red (positive)
    if (value > 1.5) return 'bg-red-500';
    if (value > 0.8) return 'bg-red-400';
    if (value > 0.3) return 'bg-red-200';
    if (value > -0.3) return 'bg-slate-50';
    if (value > -0.8) return 'bg-blue-200';
    if (value > -1.5) return 'bg-blue-400';
    return 'bg-blue-500';
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay: 0.2
  }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 flex flex-col h-full shadow-sm shadow-slate-200/50" style={{
    overflow: 'visible'
  }} data-id="element-322">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6" data-id="element-323">
        <div className="flex items-center gap-4" data-id="element-324">
          <h3 className="text-lg font-semibold text-slate-900" data-id="element-325">
            Heatmap Analysis
          </h3>
          <div className="flex bg-slate-100/80 rounded-lg p-0.5 border border-slate-200/50 w-fit" data-id="element-326">
            {(['Deviations', 'Disagreements'] as HeatmapTab[]).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-3 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-327">
                {tab}
              </button>)}
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-328">
          <span className="text-xs text-slate-500 font-medium whitespace-nowrap" data-id="element-329">
            Aggregation:
          </span>
          <select value={aggregation} onChange={e => setAggregation(e.target.value as AggregationType)} className="text-xs font-medium bg-white/80 border border-slate-200 rounded-md px-2 py-1 text-slate-700 focus:outline-none focus:ring-2 focus:ring-pink-500/20" data-id="element-330">
            <option data-id="element-331">Full (hourly)</option>
            <option data-id="element-332">Daily</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto custom-scrollbar pb-8 pt-8 relative" data-id="element-333">
        <div className="min-w-[800px]" data-id="element-334">
          {/* Header Row */}
          <div className="flex mb-4" data-id="element-335">
            <div className="w-24 flex-shrink-0" data-id="element-336"></div>
            <div className="flex-1 flex justify-between px-1" data-id="element-337">
              {aggregation === 'Full (hourly)' ?
            // Hourly view
            hours.filter(h => h % 4 === 0).map(h => {
              const date = new Date();
              date.setHours(h, 0, 0, 0);
              return <div key={h} className="w-8 text-center" data-id="element-338">
                          <div className="text-[11px] font-semibold text-slate-600 font-mono" data-id="element-339">
                            {h.toString().padStart(2, '0')}:00
                          </div>
                          <div className="text-[9px] text-slate-400 mt-0.5" data-id="element-340">
                            {date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                          </div>
                        </div>;
            }) :
            // Daily view
            days.map((date, i) => <div key={i} className="flex-1 text-center min-w-0" data-id="element-341">
                      <div className="text-[11px] font-semibold text-slate-600" data-id="element-342">
                        {date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
                      </div>
                      <div className="text-[9px] text-slate-400 mt-0.5" data-id="element-343">
                        {date.toLocaleDateString('en-US', {
                  weekday: 'short'
                })}
                      </div>
                    </div>)}
            </div>
          </div>

          {/* Heatmap Rows */}
          <div className="space-y-2 overflow-visible" data-id="element-344">
            {models.map((model, mIdx) => <div key={model} className="flex items-center group" data-id="element-345">
                <div className="w-24 flex-shrink-0 text-xs font-medium text-slate-600 truncate pr-3 text-right" data-id="element-346">
                  {model}
                </div>
                <div className="flex-1 flex gap-0.5 h-10 overflow-visible" data-id="element-347">
                  {(aggregation === 'Full (hourly)' ? hours : days).map((item, hIdx) => {
                const value = getData(mIdx, hIdx);
                const date = aggregation === 'Full (hourly)' ? (() => {
                  const d = new Date();
                  d.setHours(item as number, 0, 0, 0);
                  return d;
                })() : item as Date;
                const isLargeDeviation = Math.abs(value) > 1.0;
                return <div key={hIdx} className={`flex-1 rounded-sm transition-all hover:scale-110 hover:z-[100] relative group/cell ${getColor(value)} flex items-center justify-center overflow-visible`} data-id="element-348">
                          {/* Value inside cell */}
                          <span className={`text-[9px] font-bold ${Math.abs(value) > 0.8 ? 'text-white' : 'text-slate-600'}`} data-id="element-349">
                            {value > 0 ? '+' : ''}
                            {value.toFixed(1)}
                          </span>

                          {/* Enhanced Tooltip - smart positioning to avoid cutoff */}
                          <div className={`absolute left-1/2 -translate-x-1/2 hidden group-hover/cell:block pointer-events-none ${mIdx === 0 ? 'top-full mt-2' : 'bottom-full mb-2'}`} style={{
                    zIndex: 99999
                  }} data-id="element-350">
                            <div className="bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-xl whitespace-nowrap" data-id="element-351">
                              <div className="font-bold text-sm mb-1" data-id="element-352">
                                {value > 0 ? '+' : ''}
                                {value.toFixed(2)} deviation
                              </div>
                              <div className="text-slate-300 space-y-0.5" data-id="element-353">
                                <div className="flex items-center gap-2" data-id="element-354">
                                  <span className="text-slate-400" data-id="element-355">Model:</span>
                                  <span className="font-medium" data-id="element-356">{model}</span>
                                </div>
                                {aggregation === 'Full (hourly)' && <div className="flex items-center gap-2" data-id="element-357">
                                    <span className="text-slate-400" data-id="element-358">
                                      Time:
                                    </span>
                                    <span className="font-medium" data-id="element-359">
                                      {date.toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                                    </span>
                                  </div>}
                                <div className="flex items-center gap-2" data-id="element-360">
                                  <span className="text-slate-400" data-id="element-361">Date:</span>
                                  <span className="font-medium" data-id="element-362">
                                    {date.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                                  </span>
                                </div>
                                {isLargeDeviation && <div className="mt-1 pt-1 border-t border-slate-700 text-yellow-400 text-[10px]" data-id="element-363">
                                    ⚠ Large deviation detected
                                  </div>}
                              </div>
                            </div>
                            <div className={`w-2 h-2 bg-slate-900 rotate-45 absolute left-1/2 -translate-x-1/2 ${mIdx === 0 ? '-top-1' : '-bottom-1'}`} data-id="element-364"></div>
                          </div>
                        </div>;
              })}
                </div>
              </div>)}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-[10px] text-slate-500" data-id="element-365">
        <div className="flex items-center gap-2" data-id="element-366">
          <Info size={12} data-id="element-367" />
          <span data-id="element-368">Hover cells for details</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-369">
          <span data-id="element-370">Negative Deviation</span>
          <div className="flex gap-0.5" data-id="element-371">
            <div className="w-3 h-3 bg-blue-500 rounded-sm" data-id="element-372"></div>
            <div className="w-3 h-3 bg-blue-300 rounded-sm" data-id="element-373"></div>
            <div className="w-3 h-3 bg-slate-50 rounded-sm" data-id="element-374"></div>
            <div className="w-3 h-3 bg-red-300 rounded-sm" data-id="element-375"></div>
            <div className="w-3 h-3 bg-red-500 rounded-sm" data-id="element-376"></div>
          </div>
          <span data-id="element-377">Positive Deviation</span>
        </div>
      </div>
    </motion.div>;
}